import {UseFormSetValue, useFormContext} from 'react-hook-form'
import {useTranslation} from '@/translations/i18n.tsx'
import {InputSelect} from '@/components/commons/input-select/InputSelect'
import {MailerFormValues} from '@/features/mailer/types'
import {MailerMultiSelect} from '@/features/mailer/components/mailer-multi-select/MailerMultiSelect'
import {useGetUserSpecializations} from '@/features/users/queries/useGetUserSpecializations'
import {StyledFieldsGrid} from '@/features/mailer/components/patient-filters-panel/style'

// Staff ("Personale") filters per functional spec §5. The "Gruppo" select is
// an addition over the design doc: the BE shipped the `group` filter key
// specifically for this redesign (OPSSTD-3643 §4).
export const ConsultantFiltersPanel = () => {
    const {t} = useTranslation()
    const {watch, setValue} = useFormContext<MailerFormValues>()

    const values = watch()

    const specializationsQuery = useGetUserSpecializations({limit: 100})
    const specializationOptions = specializationsQuery.remappedData.map(specialization => ({
        value: specialization.id,
        label: specialization.name
    }))

    // Public signature is fully typed (UseFormSetValue); the internal cast works
    // around RHF's conditional PathValue types not distributing through wrappers.
    const setField: UseFormSetValue<MailerFormValues> = (name, value) =>
        setValue(name, value as never, {shouldValidate: true, shouldDirty: true})

    return (
        <StyledFieldsGrid $columns={2}>
            <MailerMultiSelect
                label={t('mailer:staff_status_label')}
                placeholder={t('mailer:multiselect_placeholder_all')}
                value={values.consultantStatus}
                onChange={value => setField('consultantStatus', value as MailerFormValues['consultantStatus'])}
                options={[
                    {value: 'operative', label: t('mailer:staff_status_operative')},
                    {value: 'candidate', label: t('mailer:staff_status_candidate')},
                    {value: 'suspended', label: t('mailer:staff_status_suspended')},
                    {value: 'absent', label: t('mailer:staff_status_absent')},
                    {value: 'terminated', label: t('mailer:staff_status_terminated')}
                ]}
            />
            <InputSelect
                label={t('mailer:staff_group_label')}
                value={values.consultantGroup}
                onChange={value => setField('consultantGroup', value)}
                optionsMap={{
                    all: 'mailer:staff_group_all',
                    referring_doctor: 'mailer:staff_group_referring_doctor',
                    therapist: 'mailer:staff_group_therapist'
                }}
            />
            <MailerMultiSelect
                label={t('mailer:contract_type_label')}
                placeholder={t('mailer:multiselect_placeholder_all')}
                value={values.consultantContractType}
                onChange={value =>
                    setField('consultantContractType', value as MailerFormValues['consultantContractType'])
                }
                options={[
                    {value: 'consultant', label: t('mailer:contract_type_consultant')},
                    {value: 'flat_rate', label: t('mailer:contract_type_flat_rate')},
                    {value: 'employee', label: t('mailer:contract_type_employee')}
                ]}
            />
            <MailerMultiSelect
                label={t('mailer:specialization_label')}
                placeholder={t('mailer:specialization_placeholder')}
                value={values.consultantSpecializationIds}
                onChange={value => setField('consultantSpecializationIds', value)}
                options={specializationOptions}
            />
            <InputSelect
                label={t('mailer:work_sector_label')}
                value={values.consultantWorkSector}
                onChange={value => setField('consultantWorkSector', value)}
                optionsMap={{
                    all: 'mailer:work_sector_all',
                    domiciliary: 'mailer:work_sector_domiciliary',
                    outpatient: 'mailer:work_sector_outpatient'
                }}
            />
        </StyledFieldsGrid>
    )
}

ConsultantFiltersPanel.displayName = 'ConsultantFiltersPanel'
